import { FC, ReactNode } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useViewportScrollLock } from '../../custom-hooks/useViewportScrollLock';

interface Props {
  title: string;
  body: ReactNode;
  badge?: string;
  keepLabel: string;
  confirmLabel: string;
  saving?: boolean;
  onKeep: () => void;
  onConfirm: () => void;
}

const ExitSlipCancelConfirm: FC<Props> = ({
  title,
  body,
  badge,
  keepLabel,
  confirmLabel,
  saving = false,
  onKeep,
  onConfirm,
}) => {
  useViewportScrollLock();

  return (
    <Dialog
      visible
      modal
      closable={false}
      showHeader={false}
      onHide={onKeep}
      pt={{
        root: {
          className:
            '!tw-w-[576px] !tw-max-w-[576px] !tw-rounded-2xl !tw-border-4 !tw-border-solid !tw-border-[#F1F0EE]',
        },
        content: { className: '!tw-rounded-2xl !tw-p-8 tw-font-atkinson' },
      }}
    >
      <div className="tw-flex tw-flex-col tw-gap-8">
        <div className="tw-flex tw-flex-col tw-gap-5">
          {badge && (
            <span className="tw-max-w-full tw-self-start tw-truncate tw-rounded-md tw-border tw-border-b-2 tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-bold tw-leading-4 tw-text-neutral-950">
              {badge}
            </span>
          )}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <span className="tw-text-2xl tw-font-bold tw-leading-8 tw-text-neutral-950">{title}</span>
            {body}
          </div>
        </div>
        <div className="tw-flex tw-justify-end tw-gap-2">
          <Button
            label={keepLabel}
            outlined
            disabled={saving}
            onClick={onKeep}
            className="!tw-h-11 !tw-rounded-full !tw-border-2 !tw-px-6 !tw-font-atkinson !tw-font-bold !tw-text-indigo-600 !tw-text-base hover:!tw-text-white"
          />
          <Button
            label={confirmLabel}
            loading={saving}
            onClick={onConfirm}
            className="!tw-h-11 !tw-rounded-full !tw-border-none !tw-bg-[#E33434] !tw-px-6 !tw-font-atkinson !tw-font-bold !tw-text-base"
          />
        </div>
      </div>
    </Dialog>
  );
};

export const exitSlipConfirmLine = (text: string) => (
  <span className="tw-text-lg tw-leading-7 tw-text-neutral-800">{text}</span>
);

interface TodayProps {
  className?: string;
  responseCount: number;
  saving?: boolean;
  onKeep: () => void;
  onConfirm: () => void;
}

export const ExitSlipCancelTodayConfirm: FC<TodayProps> = ({ className, responseCount, ...actions }) => (
  <ExitSlipCancelConfirm
    badge={className}
    title="Cancel today's exit ticket?"
    body={
      <>
        {exitSlipConfirmLine('Students will no longer be able to take the exit ticket today.')}
        {responseCount > 0 &&
          exitSlipConfirmLine(
            `The ${responseCount} ${
              responseCount === 1 ? 'response' : 'responses'
            } already submitted stay on the dashboard for the rest of today, and in the Past tab after that.`,
          )}
      </>
    }
    keepLabel="Keep lesson"
    confirmLabel="Yes, cancel lesson"
    {...actions}
  />
);

export default ExitSlipCancelConfirm;
